import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Scan, type LucideIcon } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { Alert, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { InvestigationList, investigationsQueryKey, type MovedRequest } from '../../../clinical/InvestigationList';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { OrderSection } from '../../../opd/emr/orders/OrderSection';
import { admissionIsOpen, type Admission } from '../../admission/types';
import { investigationKinds, orderPatientOf, type InvestigationKind } from '../ipd-emr-sections';

const KIND_ICON: Record<InvestigationKind, LucideIcon> = { lab: FlaskConical, radiology: Scan };

export function InvestigationsSection({ admission, icon, formKeys }: { admission: Admission; icon: LucideIcon; formKeys: readonly string[] }) {
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const composerRef = useRef<HTMLDivElement>(null);
  const kinds = investigationKinds(formKeys, { lab: hasModule('lab'), radiology: hasModule('radiology') });
  const [chosen, setChosen] = useState<InvestigationKind | null>(null);
  const [reordering, setReordering] = useState<MovedRequest | null>(null);
  const path = `/ipd/admissions/${admission.id}/investigations`;
  const patientId = admission.patient.id;
  const mayOrder = admissionIsOpen(admission) && can('order.write');
  const kind = chosen && kinds.includes(chosen) ? chosen : (kinds[0] ?? null);
  useRealtimeInvalidate([`admission:${admission.id}`], null, [investigationsQueryKey(path), ['lab-trends', patientId]]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: investigationsQueryKey(path) });
    void queryClient.invalidateQueries({ queryKey: ['lab-trends', patientId] });
  };

  const decide = useMutation({
    mutationFn: ({ request, decision }: { request: MovedRequest; decision: 'reordered' | 'dismissed' }) => post(`${path}/moved/${request.id}`, { decision }),
    onSuccess: refresh,
    onError: (caught) => toast.error(tr('ipd', 'could_not_update_the_request'), describeError(caught)),
  });

  const startReorder = (request: MovedRequest) => {
    if (!kinds.includes(request.kind)) {
      toast.error(tr('ipd', 'io_ord_reorder_unavailable'));
      return;
    }
    setChosen(request.kind);
    setReordering(request);
    requestAnimationFrame(() => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const placed = () => {
    if (!reordering) return;
    decide.mutate({ request: reordering, decision: 'reordered' });
    setReordering(null);
  };

  return (
    <>
      <ShellSectionHeading icon={icon} title={tr('ipd', 'investigations')} description={tr('ipd', 'ws_investigations_desc')} />
      <div ref={composerRef} className="scroll-mt-[calc(var(--emr-bar-h,0px)+1rem)] space-y-4">
        {kinds.length > 1 && kind && (
          <div className="w-full sm:w-80">
            <ChoiceButtons<InvestigationKind>
              label={tr('ipd', 'io_ord_kind_switch')}
              value={kind}
              onChange={(next) => {
                setChosen(next);
                if (reordering && reordering.kind !== next) setReordering(null);
              }}
              options={kinds.map((value) => ({ value, label: tr('ipd', `io_kind_${value}`), icon: KIND_ICON[value] }))}
            />
          </div>
        )}
        {reordering && mayOrder && (
          <Alert>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{tr('ipd', 're_order_x', { label: reordering.label })}</span>
              <Button size="sm" variant="ghost" onClick={() => setReordering(null)}>
                {tr('ipd', 'io_ord_reorder_cancel')}
              </Button>
            </div>
          </Alert>
        )}
        {kind && (
          <OrderSection
            key={`${kind}:${reordering?.id ?? ''}`}
            kind={kind}
            scope={{ admissionId: admission.id }}
            patient={orderPatientOf(admission.patient)}
            readOnly={!mayOrder}
            initialSearch={reordering?.kind === kind ? reordering.label : undefined}
            onChanged={refresh}
            onPlaced={placed}
          />
        )}
      </div>
      <InvestigationList
        path={path}
        patientId={patientId}
        showCumulative
        onReorder={mayOrder ? startReorder : undefined}
        onDismiss={mayOrder ? (request) => decide.mutate({ request, decision: 'dismissed' }) : undefined}
      />
    </>
  );
}
